import { useEffect, useMemo, useState } from "react";
import {
  groupCoachEdges,
  type CoachAthleteEdge,
} from "@jackedlogs/shared";

import { apiFetch } from "./api";

// The coach experience's gate AND its roster (CO-8), from ONE read of
// /api/me/coach-edges. `groupCoachEdges` owns what "an athlete I coach" means —
// mobile's CoachEdgesContext groups the same rows with the same function, so the
// two platforms can't drift on edge semantics (CN-coach-edges-fetch).
//
// `ready` exists for the experience gate, not for rendering: `isCoach` starts
// false and only becomes true once this read lands, so a consumer that resets an
// unavailable experience MUST wait for it or it will clobber a persisted 'coach'
// choice on every refresh (CO-15). A failed read degrades to "not a coach" and
// never blocks sign-in — but it still resolves, so the gate stops waiting.
export function useCoachStatus(mySub: string): {
  isCoach: boolean;
  athleteSubs: ReadonlyArray<string>;
  ready: boolean;
} {
  const [edges, setEdges] = useState<ReadonlyArray<CoachAthleteEdge>>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    setReady(false);
    apiFetch<CoachAthleteEdge[]>("/api/me/coach-edges")
      .then((rows) => {
        if (!alive) return;
        setEdges(rows);
        setReady(true);
      })
      .catch(() => {
        if (!alive) return;
        setEdges([]);
        setReady(true);
      });
    return () => {
      alive = false;
    };
  }, [mySub]);

  const athleteSubs = useMemo(
    () => groupCoachEdges(edges, mySub).athletesActive.map((e) => e.athleteSub),
    [edges, mySub],
  );

  return { isCoach: athleteSubs.length > 0, athleteSubs, ready };
}
